import { React, useEffect, useMemo, useRef, useState, forwardRef } from 'react';
import MaterialTable from '@material-table/core';
import AddBox from '@material-ui/icons/AddBox';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Print from '@material-ui/icons/Print';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';
import ChangeHistoryRounded from '@material-ui/icons/Update';
import { Button, FormControl, FormGroup, FormLabel, FormSelect, Modal } from 'react-bootstrap';
import swal from 'sweetalert';
import Cookies from 'universal-cookie/es6';
import { Redirect } from 'react-router-dom';
import Navbar2 from './Navbar';
import { getSalaImagen } from '../salaImagenes';

const api = process.env.REACT_APP_API;

const ORDEN_EDIFICIOS = ['Torre 1', 'Torre 2', 'CBB', 'CBC', 'Data Center', 'Plaza'];

const pad2 = (value) => String(value).padStart(2, '0');

const toDateKey = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
  }
  return '';
};

const parseDateKey = (value) => {
  const safeValue = toDateKey(value);
  if (!safeValue) return null;
  const [year, month, day] = safeValue.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const toMinutes = (value) => {
  if (!value || typeof value !== 'string') return null;
  const [hourRaw, minuteRaw] = value.slice(0, 5).split(':').map(Number);
  if (Number.isNaN(hourRaw) || Number.isNaN(minuteRaw)) return null;
  return hourRaw * 60 + minuteRaw;
};

const getDurationHours = (inicio, fin) => {
  const startMinutes = toMinutes(inicio);
  const endMinutesRaw = toMinutes(fin);
  if (startMinutes === null || endMinutesRaw === null) return 0;

  let endMinutes = endMinutesRaw;
  if (endMinutes < startMinutes) endMinutes += 24 * 60;

  return (endMinutes - startMinutes) / 60;
};

const formatHours = (value) => {
  if (!value) return '0 h';
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded} h` : `${rounded.toFixed(1)} h`;
};

const getUserDisplayName = (user) => {
  const nombre = String(user?.nombre || '').trim();
  const apellido = String(user?.apellido || '').trim();
  const fullName = `${nombre} ${apellido}`.trim();
  if (fullName) return fullName;

  const nombreUsuario = String(user?.nombre_Usuario || '').trim();
  if (nombreUsuario) return nombreUsuario;

  return String(user?.correo_Electronico || '').trim();
};

const getSalaConEdificio = (row) => {
  const sala = String(row?.nombre_Sala || '').trim();
  const edificio = String(row?.edificio || '').trim();
  if (sala && edificio) return `${sala} (${edificio})`;
  if (sala) return sala;
  if (edificio) return edificio;
  return 'Sin sala';
};

const buildTopLabel = (rows, getKey) => {
  const counts = {};

  rows.forEach((row) => {
    const key = getKey(row);
    if (!key) return;
    counts[key] = (counts[key] || 0) + 1;
  });

  const [first] = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return first ? `${first[0]} (${first[1]})` : 'Sin datos';
};

const getPeriodRange = (periodo, fechaBase, mesBase, anioBase) => {
  if (periodo === 'historico') {
    return { start: null, end: null, label: 'Histórico completo' };
  }

  if (periodo === 'dia') {
    const selectedDate = parseDateKey(fechaBase);
    if (!selectedDate) return { start: null, end: null, label: 'Día no válido' };
    return { start: selectedDate, end: selectedDate, label: `Día ${toDateKey(selectedDate)}` };
  }

  if (periodo === 'semana') {
    const selectedDate = parseDateKey(fechaBase);
    if (!selectedDate) return { start: null, end: null, label: 'Semana no válida' };

    const start = new Date(selectedDate);
    const offset = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - offset);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    return {
      start,
      end,
      label: `Semana ${toDateKey(start)} al ${toDateKey(end)}`,
    };
  }

  if (periodo === 'mes') {
    if (!mesBase || !mesBase.includes('-')) return { start: null, end: null, label: 'Mes no válido' };
    const [yearRaw, monthRaw] = mesBase.split('-').map(Number);
    if (!yearRaw || !monthRaw) return { start: null, end: null, label: 'Mes no válido' };

    const start = new Date(yearRaw, monthRaw - 1, 1);
    const end = new Date(yearRaw, monthRaw, 0);

    return {
      start,
      end,
      label: `Mes ${mesBase}`,
    };
  }

  if (periodo === 'anio') {
    const yearNumber = Number(anioBase);
    if (!yearNumber) return { start: null, end: null, label: 'Año no válido' };

    return {
      start: new Date(yearNumber, 0, 1),
      end: new Date(yearNumber, 11, 31),
      label: `Año ${yearNumber}`,
    };
  }

  return { start: null, end: null, label: 'Sin rango' };
};

const isWithinRange = (dia, range) => {
  if (!range.start || !range.end) return true;
  const rowDate = parseDateKey(dia);
  if (!rowDate) return false;
  return rowDate >= range.start && rowDate <= range.end;
};

function Gestion() {
  const cookies = new Cookies();

  const normalizeRole = (value) => {
    if (value === undefined || value === null) return '';
    const raw = String(value);
    try {
      return decodeURIComponent(raw)
        .replace(/^['\"]|['\"]$/g, '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
    } catch (e) {
      return raw
        .replace(/^['\"]|['\"]$/g, '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
    }
  };

  const rol = normalizeRole(cookies.get('nombre_Rol'));
  const idRol = String(cookies.get('id_rol') || '').trim();
  const isBasicUser = rol.includes('usuario') || idRol === '1';

  const [vistaActiva, setVistaActiva] = useState('usuarios');

  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [cambio, setCambio] = useState(false);

  const [solicitudes, setSolicitudes] = useState([]);

  const isSuperAdmin = rol === 'super admin';
  const [salas, setSalas] = useState([]);
  const [salasLoading, setSalasLoading] = useState(false);
  const [salaActualizando, setSalaActualizando] = useState(null);

  const [periodoControl, setPeriodoControl] = useState('historico');
  const [fechaControl, setFechaControl] = useState(() => toDateKey(new Date()));
  const [mesControl, setMesControl] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${pad2(today.getMonth() + 1)}`;
  });
  const [anioControl, setAnioControl] = useState(() => String(new Date().getFullYear()));
  const [usuarioControl, setUsuarioControl] = useState('');
  const [filasControl, setFilasControl] = useState(8);
  const [paginaControl, setPaginaControl] = useState(0);

  const [vistaModal, setVistaModal] = useState(false);
  const [nombreRolActual, setNombreRolActual] = useState('');
  const [correoUsuario, setCorreoUsuario] = useState('');
  const [nuevoRol, setNuevoRol] = useState('');

  const getUsuarios = async () => {
    try {
      const res = await fetch(`${api}/usuarios`);
      const data = await res.json();
      setUsuarios(data);
    } catch (e) {
      console.error(e);
    }
  };

  const getRoles = async () => {
    try {
      const res = await fetch(`${api}/roles`);
      const data = await res.json();
      setRoles(data);
    } catch (e) {
      console.error(e);
    }
  };

  const getSolicitudes = async () => {
    try {
      const res = await fetch(`${api}/mostrar`);
      const data = await res.json();
      data.forEach((result) => {
        const codigo = String(result.id_Solicitud || '');
        result.id_Solicitud = codigo.startsWith('RS') ? codigo : `RS${codigo}`;
        result.dia = result.dia ? result.dia.slice(0, 10) : '';
      });
      setSolicitudes(data);
    } catch (e) {
      console.error(e);
    }
  };

  const getSalas = async () => {
    setSalasLoading(true);
    try {
      const res = await fetch(`${api}/salas`);
      const data = await res.json();
      setSalas(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    }
    setSalasLoading(false);
  };

  const toggleEstadoSala = async (sala) => {
    const estadoActual = String(sala.estado || 'Disponible').trim();
    const nuevoEstado = estadoActual === 'Disponible' ? 'No Disponible' : 'Disponible';

    const confirmado = await swal({
      title: 'Confirmar cambio',
      text: `¿Cambiar "${sala.nombre_Sala}" (${sala.edificio}) a ${nuevoEstado}?`,
      icon: 'warning',
      buttons: ['Cancelar', 'Confirmar'],
      dangerMode: nuevoEstado === 'No Disponible',
    });
    if (!confirmado) return;

    setSalaActualizando(sala.id_Sala);
    try {
      const res = await fetch(`${api}/actualizarEstadoSala`, {
        method: 'put',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_Sala: sala.id_Sala, estado: nuevoEstado }),
      });

      if (!res.ok) throw new Error('No se pudo actualizar');

      setSalas((prev) => prev.map((s) => (
        s.id_Sala === sala.id_Sala ? { ...s, estado: nuevoEstado } : s
      )));

      swal({
        title: 'Estado Actualizado',
        text: `${sala.nombre_Sala} (${sala.edificio}) ahora está ${nuevoEstado}.`,
        icon: 'success',
        button: 'Aceptar',
      });
    } catch (e) {
      console.error(e);
      swal({
        title: 'Error',
        text: 'No se pudo actualizar el estado de la sala.',
        icon: 'error',
        button: 'Aceptar',
      });
    }
    setSalaActualizando(null);
  };

  useEffect(() => {
    getUsuarios();
  }, [cambio]);

  useEffect(() => {
    getSolicitudes();
  }, []);

  useEffect(() => {
    if (isSuperAdmin) getSalas();
  }, [isSuperAdmin]);

  const normalizarEdificio = (valor) => String(valor || '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/([A-Za-zÁÉÍÓÚáéíóú])(\d)/, '$1 $2');

  const salasAgrupadas = useMemo(
    () => salas.map((s) => ({ ...s, edificio: normalizarEdificio(s.edificio) })),
    [salas]
  );

  const resumenSalas = useMemo(() => {
    const total = salas.length;
    const disponibles = salas.filter((s) => String(s.estado || '').trim() === 'Disponible').length;
    return { total, disponibles, noDisponibles: total - disponibles };
  }, [salas]);

  const salasPorEdificio = useMemo(() => {
    const grouped = {};
    salasAgrupadas.forEach((s) => {
      if (!grouped[s.edificio]) grouped[s.edificio] = [];
      grouped[s.edificio].push(s);
    });
    Object.keys(grouped).forEach((edif) => {
      grouped[edif].sort((a, b) => String(a.nombre_Sala || '').localeCompare(String(b.nombre_Sala || '')));
    });
    return grouped;
  }, [salasAgrupadas]);

  const edificiosDisponibles = useMemo(() => {
    return Object.keys(salasPorEdificio).sort((a, b) => {
      const idxA = ORDEN_EDIFICIOS.indexOf(a);
      const idxB = ORDEN_EDIFICIOS.indexOf(b);
      if (idxA === -1 && idxB === -1) return a.localeCompare(b);
      if (idxA === -1) return 1;
      if (idxB === -1) return -1;
      return idxA - idxB;
    });
  }, [salasPorEdificio]);

  const [edificioActivoSalas, setEdificioActivoSalas] = useState('');

  useEffect(() => {
    if (!edificioActivoSalas && edificiosDisponibles.length) {
      setEdificioActivoSalas(edificiosDisponibles[0]);
    }
  }, [edificiosDisponibles, edificioActivoSalas]);

  const carruselSalasRef = useRef(null);

  const scrollCarruselSalas = (direccion) => {
    if (!carruselSalasRef.current) return;
    carruselSalasRef.current.scrollBy({ left: direccion * 300, behavior: 'smooth' });
  };

  const usuariosBase = useMemo(() => {
    return usuarios
      .filter((user) => {
        const userRole = normalizeRole(user.nombre_Rol);
        const userRoleId = String(user.id_rol || '').trim();
        return userRole.includes('usuario') || userRoleId === '1';
      })
      .sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b)));
  }, [usuarios]);

  useEffect(() => {
    if (!usuariosBase.length) {
      setUsuarioControl('');
      return;
    }

    const currentExists = usuariosBase.some((user) => user.correo_Electronico === usuarioControl);
    if (!currentExists) {
      setUsuarioControl(usuariosBase[0].correo_Electronico);
    }
  }, [usuariosBase, usuarioControl]);

  const rangoControl = useMemo(
    () => getPeriodRange(periodoControl, fechaControl, mesControl, anioControl),
    [periodoControl, fechaControl, mesControl, anioControl]
  );

  const solicitudesUsuariosBase = useMemo(() => {
    const allowedEmails = new Set(usuariosBase.map((user) => String(user.correo_Electronico || '').trim().toLowerCase()));
    return solicitudes.filter((row) => {
      const email = String(row.correo_Electronico || '').trim().toLowerCase();
      if (!allowedEmails.has(email)) return false;
      return isWithinRange(row.dia, rangoControl);
    });
  }, [solicitudes, usuariosBase, rangoControl]);

  const solicitudesPorCorreo = useMemo(() => {
    const grouped = {};
    solicitudesUsuariosBase.forEach((row) => {
      const email = String(row.correo_Electronico || '').trim().toLowerCase();
      if (!grouped[email]) grouped[email] = [];
      grouped[email].push(row);
    });
    return grouped;
  }, [solicitudesUsuariosBase]);

  const usuariosControlData = useMemo(() => {
    return usuariosBase.map((user) => {
      const email = String(user.correo_Electronico || '').trim().toLowerCase();
      const rows = solicitudesPorCorreo[email] || [];
      const horasTotales = rows.reduce((acc, row) => acc + getDurationHours(row.hora_Inicio, row.hora_Fin), 0);
      return {
        ...user,
        solicitudesPeriodo: rows.length,
        horasPeriodo: formatHours(horasTotales),
        salaTop: buildTopLabel(rows, getSalaConEdificio),
      };
    });
  }, [usuariosBase, solicitudesPorCorreo]);

  const usuarioControlActual = useMemo(
    () => usuariosBase.find((user) => user.correo_Electronico === usuarioControl) || null,
    [usuariosBase, usuarioControl]
  );

  const solicitudesUsuarioActual = useMemo(() => {
    const email = String(usuarioControl || '').trim().toLowerCase();
    const rows = solicitudesPorCorreo[email] || [];
    return [...rows].sort((a, b) => {
      const dateCompare = String(b.dia || '').localeCompare(String(a.dia || ''));
      if (dateCompare !== 0) return dateCompare;
      return String(b.hora_Inicio || '').localeCompare(String(a.hora_Inicio || ''));
    });
  }, [solicitudesPorCorreo, usuarioControl]);

  useEffect(() => {
    setPaginaControl(0);
  }, [usuarioControl, periodoControl, fechaControl, mesControl, anioControl, filasControl]);

  const resumenControl = useMemo(() => {
    const totalSolicitudes = solicitudesUsuarioActual.length;
    const horasSolicitadas = solicitudesUsuarioActual.reduce(
      (acc, row) => acc + getDurationHours(row.hora_Inicio, row.hora_Fin),
      0
    );
    const conteoEstados = solicitudesUsuarioActual.reduce(
      (acc, row) => {
        const estado = String(row.confirmacion || 'Sin estado');
        acc[estado] = (acc[estado] || 0) + 1;
        return acc;
      },
      {}
    );

    return {
      totalSolicitudes,
      horasSolicitadas,
      promedioHoras: totalSolicitudes ? horasSolicitadas / totalSolicitudes : 0,
      salaTop: buildTopLabel(solicitudesUsuarioActual, getSalaConEdificio),
      horarioTop: buildTopLabel(
        solicitudesUsuarioActual,
        (row) => `${String(row.hora_Inicio || '').slice(0, 5)} - ${String(row.hora_Fin || '').slice(0, 5)}`
      ),
      estados: conteoEstados,
    };
  }, [solicitudesUsuarioActual]);

  const topSalasControl = useMemo(() => {
    const counts = {};
    solicitudesUsuarioActual.forEach((row) => {
      const key = getSalaConEdificio(row);
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [solicitudesUsuarioActual]);

  const topHorariosControl = useMemo(() => {
    const counts = {};
    solicitudesUsuarioActual.forEach((row) => {
      const key = `${String(row.hora_Inicio || '').slice(0, 5)} - ${String(row.hora_Fin || '').slice(0, 5)}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [solicitudesUsuarioActual]);

  const totalPaginasControl = Math.max(1, Math.ceil(solicitudesUsuarioActual.length / filasControl));
  const paginaControlSegura = Math.min(paginaControl, totalPaginasControl - 1);
  const solicitudesUsuarioPaginadas = useMemo(() => {
    const start = paginaControlSegura * filasControl;
    return solicitudesUsuarioActual.slice(start, start + filasControl);
  }, [solicitudesUsuarioActual, paginaControlSegura, filasControl]);

  const saveChange = async () => {
    if (nuevoRol === '' || nuevoRol === 'Seleccione Una Opción') {
      swal({
        title: 'Validación de Datos',
        text: 'No seleccionó una opción en el campo de Nuevo Rol.',
        icon: 'warning',
        button: 'Aceptar',
      });
      return;
    }

    if (nuevoRol === nombreRolActual) {
      swal({
        title: 'Validación de Datos',
        text: 'El usuario ya cuenta con ese rol asignado.',
        icon: 'error',
        button: 'Aceptar',
      });
      return;
    }

    try {
      const res = await fetch(`${api}/actualizarRol`, {
        method: 'put',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nuevoR: nuevoRol,
          nombre_Usuario: correoUsuario,
        }),
      });

      const data = await res.json();
      if (data === 'Actualizado') {
        swal({
          title: 'Rol de Usuario Modificado',
          text: 'Se ha modificado el rol del usuario correctamente.',
          icon: 'success',
          button: 'Aceptar',
        });
        setCambio((v) => !v);
        setVistaModal(false);
      } else {
        swal({
          title: 'No se pudo modificar el rol del usuario',
          icon: 'error',
          button: 'Aceptar',
        });
      }
    } catch (e) {
      console.error(e);
      swal({
        title: 'Error al actualizar rol',
        icon: 'error',
        button: 'Aceptar',
      });
    }
  };

  const tableIcons = {
    Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
    Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
    Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
    Print: forwardRef((props, ref) => <Print {...props} ref={ref} />),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
    ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
    ChangeHistoryRounded: forwardRef((props, ref) => <ChangeHistoryRounded {...props} ref={ref} />),
  };

  const columnsUsuarios = [
    { title: 'Nombre', field: 'nombre' },
    { title: 'Apellido', field: 'apellido' },
    { title: 'Nombre Usuario', field: 'nombre_Usuario' },
    { title: 'N° Teléfono', field: 'telefono' },
    { title: 'Correo Electrónico', field: 'correo_Electronico' },
    { title: 'Nombre Institución', field: 'nombre_Institucion' },
    { title: 'Área Trabajo', field: 'nombre_Are' },
    { title: 'Nombre de Rol', field: 'nombre_Rol' },
  ];

  const columnsSolicitudes = [
    { title: 'Código de Solicitud', field: 'id_Solicitud' },
    { title: 'Edificio', field: 'edificio' },
    { title: 'Nombre de Sala', field: 'nombre_Sala' },
    { title: 'N° de piso', field: 'n_Piso' },
    { title: 'Tamaño de Sala', field: 'Tamaño_sala' },
    { title: 'Día', field: 'dia' },
    { title: 'Hora de Inicio', field: 'hora_Inicio' },
    { title: 'Hora de Finalización', field: 'hora_Fin' },
    { title: 'Estado', field: 'confirmacion' },
    { title: 'Usuario', field: 'correo_Electronico' },
  ];

  const columnsControlUsuarios = [
    {
      title: 'Usuario',
      render: (row) => getUserDisplayName(row),
    },
    { title: 'Correo', field: 'correo_Electronico' },
    { title: 'Institución', field: 'nombre_Institucion' },
    { title: 'Solicitudes', field: 'solicitudesPeriodo', type: 'numeric' },
    { title: 'Horas', field: 'horasPeriodo' },
    { title: 'Sala más reservada', field: 'salaTop' },
  ];

  if (isBasicUser) {
    return <Redirect to="/inicio" />;
  }

  return (
    <div>
      <Navbar2 />

      <div style={{ maxWidth: 1320, margin: '0 auto', padding: '16px' }}>
        <div style={{
          display: 'flex',
          gap: 8,
          marginBottom: 12,
          background: '#f1f5f9',
          borderRadius: 10,
          padding: 6,
          width: 'fit-content',
        }}>
          <button
            onClick={() => setVistaActiva('usuarios')}
            style={{
              border: 'none',
              borderRadius: 8,
              padding: '8px 14px',
              fontWeight: 700,
              cursor: 'pointer',
              background: vistaActiva === 'usuarios' ? '#2563eb' : 'transparent',
              color: vistaActiva === 'usuarios' ? '#fff' : '#334155',
            }}
          >
            👥 Usuarios
          </button>
          <button
            onClick={() => setVistaActiva('solicitudes')}
            style={{
              border: 'none',
              borderRadius: 8,
              padding: '8px 14px',
              fontWeight: 700,
              cursor: 'pointer',
              background: vistaActiva === 'solicitudes' ? '#2563eb' : 'transparent',
              color: vistaActiva === 'solicitudes' ? '#fff' : '#334155',
            }}
          >
            📂 Todas las Solicitudes
          </button>
          <button
            onClick={() => setVistaActiva('control-reserva')}
            style={{
              border: 'none',
              borderRadius: 8,
              padding: '8px 14px',
              fontWeight: 700,
              cursor: 'pointer',
              background: vistaActiva === 'control-reserva' ? '#2563eb' : 'transparent',
              color: vistaActiva === 'control-reserva' ? '#fff' : '#334155',
            }}
          >
            📊 Control de reserva
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => setVistaActiva('disponibilidad-salas')}
              style={{
                border: 'none',
                borderRadius: 8,
                padding: '8px 14px',
                fontWeight: 700,
                cursor: 'pointer',
                background: vistaActiva === 'disponibilidad-salas' ? '#2563eb' : 'transparent',
                color: vistaActiva === 'disponibilidad-salas' ? '#fff' : '#334155',
              }}
            >
              🚪 Disponibilidad de Salas
            </button>
          )}
        </div>

        {vistaActiva === 'usuarios' && (
          <div className="table-responsive">
            <MaterialTable
              options={{
                actionsColumnIndex: -1,
                headerStyle: { backgroundColor: '#4484ce', color: '#FFF' },
                maxBodyHeight: '620px',
              }}
              icons={tableIcons}
              data={usuarios}
              columns={columnsUsuarios}
              title="Gestión de Usuarios"
              localization={{
                pagination: {
                  labelDisplayedRows: '{from} - {to} de {count}',
                  labelRowsPerPage: 'resultados por página',
                  firstAriaLabel: 'Primera página',
                  firstTooltip: 'Primera página',
                  previousAriaLabel: 'página anterior',
                  previousTooltip: 'página anterior',
                  nextAriaLabel: 'Siguiente página',
                  nextTooltip: 'Siguiente página',
                  lastAriaLabel: 'Última página',
                  lastTooltip: 'Última página',
                  labelRowsSelect: 'Filas',
                },
                toolbar: { nRowsSelected: '{0} row(s) selected' },
                header: { actions: 'Acción' },
                body: {
                  emptyDataSourceMessage: 'No se encontraron registros',
                  filterRow: { filterTooltip: 'Filter' },
                },
              }}
              actions={[
                {
                  icon: ChangeHistoryRounded,
                  tooltip: 'Administrar Rol de Usuario',
                  onClick: (_, rowdata) => {
                    if (rol === 'administrador datacenter') {
                      setNombreRolActual(rowdata.nombre_Rol);
                      setCorreoUsuario(rowdata.correo_Electronico);
                      setNuevoRol('');
                      setVistaModal(true);
                      getRoles();
                    } else {
                      swal({
                        title: 'Su usuario no tiene permisos para esta opción',
                        icon: 'error',
                        button: 'Aceptar',
                      });
                    }
                  },
                },
              ]}
            />
          </div>
        )}

        {vistaActiva === 'solicitudes' && (
          <div className="table-responsive">
            <MaterialTable
              options={{
                actionsColumnIndex: -1,
                headerStyle: { backgroundColor: '#4484ce', color: '#FFF' },
                maxBodyHeight: '620px',
              }}
              icons={tableIcons}
              data={solicitudes}
              columns={columnsSolicitudes}
              title="Todas las Solicitudes"
              localization={{
                pagination: {
                  labelDisplayedRows: '{from} - {to} de {count}',
                  labelRowsPerPage: 'resultados por página',
                  firstAriaLabel: 'Primera página',
                  firstTooltip: 'Primera página',
                  previousAriaLabel: 'página anterior',
                  previousTooltip: 'página anterior',
                  nextAriaLabel: 'Siguiente página',
                  nextTooltip: 'Siguiente página',
                  lastAriaLabel: 'Última página',
                  lastTooltip: 'Última página',
                  labelRowsSelect: 'Filas',
                },
                toolbar: { nRowsSelected: '{0} row(s) selected' },
                header: { actions: 'Acción' },
                body: {
                  emptyDataSourceMessage: 'No se encontraron registros',
                  filterRow: { filterTooltip: 'Filter' },
                },
              }}
            />
          </div>
        )}

        {vistaActiva === 'control-reserva' && (
          <div style={{ display: 'grid', gap: 16 }}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              padding: 18,
              boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', alignItems: 'end' }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Control de reserva</div>
                  <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
                    Usuarios base con rol Usuario y su comportamiento de reservas por periodo.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end' }}>
                  <FormGroup>
                    <FormLabel style={{ marginBottom: 4, fontWeight: 700, color: '#334155' }}>Periodo</FormLabel>
                    <FormSelect value={periodoControl} onChange={(e) => setPeriodoControl(e.target.value)} style={{ minWidth: 180 }}>
                      <option value="historico">Histórico</option>
                      <option value="anio">Año</option>
                      <option value="mes">Mes</option>
                      <option value="semana">Semana</option>
                      <option value="dia">Día</option>
                    </FormSelect>
                  </FormGroup>

                  {periodoControl === 'anio' && (
                    <FormGroup>
                      <FormLabel style={{ marginBottom: 4, fontWeight: 700, color: '#334155' }}>Año</FormLabel>
                      <FormControl type="number" value={anioControl} onChange={(e) => setAnioControl(e.target.value)} style={{ minWidth: 120 }} />
                    </FormGroup>
                  )}

                  {periodoControl === 'mes' && (
                    <FormGroup>
                      <FormLabel style={{ marginBottom: 4, fontWeight: 700, color: '#334155' }}>Mes</FormLabel>
                      <FormControl type="month" value={mesControl} onChange={(e) => setMesControl(e.target.value)} style={{ minWidth: 170 }} />
                    </FormGroup>
                  )}

                  {(periodoControl === 'semana' || periodoControl === 'dia') && (
                    <FormGroup>
                      <FormLabel style={{ marginBottom: 4, fontWeight: 700, color: '#334155' }}>
                        {periodoControl === 'semana' ? 'Fecha de referencia' : 'Fecha'}
                      </FormLabel>
                      <FormControl type="date" value={fechaControl} onChange={(e) => setFechaControl(e.target.value)} style={{ minWidth: 170 }} />
                    </FormGroup>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 12, fontSize: 13, color: '#475569', fontWeight: 600 }}>
                Rango aplicado: {rangoControl.label}
              </div>
            </div>

            <div className="table-responsive">
              <MaterialTable
                options={{
                  actionsColumnIndex: -1,
                  headerStyle: { backgroundColor: '#4484ce', color: '#FFF' },
                  maxBodyHeight: '360px',
                  pageSize: 8,
                  pageSizeOptions: [8, 15, 25],
                  rowStyle: (rowData) => ({
                    backgroundColor: rowData.correo_Electronico === usuarioControl ? '#eff6ff' : '#fff',
                    cursor: 'pointer',
                  }),
                }}
                icons={tableIcons}
                data={usuariosControlData}
                columns={columnsControlUsuarios}
                title="Usuarios con rol Usuario"
                onRowClick={(_, rowData) => setUsuarioControl(rowData.correo_Electronico)}
                localization={{
                  pagination: {
                    labelDisplayedRows: '{from} - {to} de {count}',
                    labelRowsPerPage: 'resultados por página',
                    firstAriaLabel: 'Primera página',
                    firstTooltip: 'Primera página',
                    previousAriaLabel: 'página anterior',
                    previousTooltip: 'página anterior',
                    nextAriaLabel: 'Siguiente página',
                    nextTooltip: 'Siguiente página',
                    lastAriaLabel: 'Última página',
                    lastTooltip: 'Última página',
                    labelRowsSelect: 'Filas',
                  },
                  toolbar: { nRowsSelected: '{0} row(s) selected' },
                  header: { actions: 'Acción' },
                  body: {
                    emptyDataSourceMessage: 'No se encontraron usuarios con rol Usuario',
                    filterRow: { filterTooltip: 'Filter' },
                  },
                }}
              />
            </div>

            {usuarioControlActual && (
              <div style={{
                background: '#fff',
                borderRadius: 12,
                border: '1px solid #e2e8f0',
                padding: 18,
                boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{getUserDisplayName(usuarioControlActual)}</div>
                    <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                      {usuarioControlActual.correo_Electronico} · {usuarioControlActual.nombre_Institucion || 'Sin institución'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {Object.entries(resumenControl.estados).map(([estado, cantidad]) => (
                      <span
                        key={estado}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 999,
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#334155',
                        }}
                      >
                        {estado}: {cantidad}
                      </span>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12, marginBottom: 18 }}>
                  {[
                    { label: 'Solicitudes', value: resumenControl.totalSolicitudes },
                    { label: 'Horas solicitadas', value: formatHours(resumenControl.horasSolicitadas) },
                    { label: 'Promedio por solicitud', value: formatHours(resumenControl.promedioHoras) },
                    { label: 'Sala más reservada', value: resumenControl.salaTop },
                    { label: 'Horario más usado', value: resumenControl.horarioTop },
                  ].map((item) => (
                    <div
                      key={item.label}
                      style={{
                        background: 'linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%)',
                        border: '1px solid #dbeafe',
                        borderRadius: 12,
                        padding: 14,
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {item.label}
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginTop: 6, lineHeight: 1.2 }}>
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, marginBottom: 18 }}>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 14 }}>
                    <div style={{ fontWeight: 800, color: '#1e293b', marginBottom: 10 }}>Salas más reservadas</div>
                    {topSalasControl.length === 0 ? (
                      <div style={{ color: '#64748b', fontSize: 13 }}>Sin datos en el periodo seleccionado.</div>
                    ) : (
                      topSalasControl.map(([label, count]) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                          <span style={{ color: '#334155', fontWeight: 600 }}>{label}</span>
                          <span style={{ color: '#2563eb', fontWeight: 800 }}>{count}</span>
                        </div>
                      ))
                    )}
                  </div>

                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 14 }}>
                    <div style={{ fontWeight: 800, color: '#1e293b', marginBottom: 10 }}>Horarios más usados</div>
                    {topHorariosControl.length === 0 ? (
                      <div style={{ color: '#64748b', fontSize: 13 }}>Sin datos en el periodo seleccionado.</div>
                    ) : (
                      topHorariosControl.map(([label, count]) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                          <span style={{ color: '#334155', fontWeight: 600 }}>{label}</span>
                          <span style={{ color: '#2563eb', fontWeight: 800 }}>{count}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                  <div style={{
                    padding: '14px 16px',
                    background: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}>
                    <div style={{ fontWeight: 800, color: '#0f172a' }}>
                      Registro de solicitudes del periodo
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Filas</span>
                      <FormSelect
                        value={filasControl}
                        onChange={(e) => setFilasControl(Number(e.target.value))}
                        style={{ width: 90, minWidth: 90 }}
                      >
                        <option value={8}>8</option>
                        <option value={15}>15</option>
                        <option value={25}>25</option>
                      </FormSelect>
                    </div>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc', color: '#334155' }}>
                          {['Código', 'Día', 'Horario', 'Sala', 'Estado', 'Duración'].map((title) => (
                            <th key={title} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 12, borderBottom: '1px solid #e2e8f0' }}>{title}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {solicitudesUsuarioActual.length === 0 ? (
                          <tr>
                            <td colSpan={6} style={{ padding: '16px 14px', color: '#64748b' }}>
                              No hay solicitudes para este usuario en el periodo seleccionado.
                            </td>
                          </tr>
                        ) : (
                          solicitudesUsuarioPaginadas.map((row) => (
                            <tr key={`${row.id_Solicitud}-${row.dia}-${row.hora_Inicio}`}>
                              <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', fontWeight: 700, color: '#1e293b' }}>{row.id_Solicitud}</td>
                              <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>{row.dia}</td>
                              <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>{row.hora_Inicio} - {row.hora_Fin}</td>
                              <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>{getSalaConEdificio(row)}</td>
                              <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>{row.confirmacion}</td>
                              <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>{formatHours(getDurationHours(row.hora_Inicio, row.hora_Fin))}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {solicitudesUsuarioActual.length > 0 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 12,
                      flexWrap: 'wrap',
                      padding: '12px 16px',
                      borderTop: '1px solid #e2e8f0',
                      background: '#fff',
                    }}>
                      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                        Mostrando {paginaControlSegura * filasControl + 1} - {Math.min((paginaControlSegura + 1) * filasControl, solicitudesUsuarioActual.length)} de {solicitudesUsuarioActual.length}
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <Button
                          variant="light"
                          disabled={paginaControlSegura === 0}
                          onClick={() => setPaginaControl((prev) => Math.max(prev - 1, 0))}
                        >
                          Anterior
                        </Button>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>
                          Página {paginaControlSegura + 1} de {totalPaginasControl}
                        </span>
                        <Button
                          variant="light"
                          disabled={paginaControlSegura >= totalPaginasControl - 1}
                          onClick={() => setPaginaControl((prev) => Math.min(prev + 1, totalPaginasControl - 1))}
                        >
                          Siguiente
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {vistaActiva === 'disponibilidad-salas' && isSuperAdmin && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
              {[
                { label: 'Total de Salas', value: resumenSalas.total, color: '#0f172a' },
                { label: 'Disponibles', value: resumenSalas.disponibles, color: '#065f46' },
                { label: 'No Disponibles', value: resumenSalas.noDisponibles, color: '#991b1b' },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 12,
                    padding: 14,
                    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: item.color, marginTop: 6 }}>
                    {item.value}
                  </div>
                </div>
              ))}
            </div>

            {salasLoading ? (
              <div style={{ textAlign: 'center', padding: 30, color: '#64748b' }}>Cargando…</div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
                  {edificiosDisponibles.map((edif) => (
                    <button
                      key={edif}
                      onClick={() => setEdificioActivoSalas(edif)}
                      style={{
                        border: 'none',
                        borderRadius: 999,
                        padding: '9px 18px',
                        fontWeight: 700,
                        fontSize: 13,
                        letterSpacing: '0.03em',
                        cursor: 'pointer',
                        background: edificioActivoSalas === edif ? '#2563eb' : '#f1f5f9',
                        color: edificioActivoSalas === edif ? '#fff' : '#334155',
                        textTransform: 'uppercase',
                      }}
                    >
                      {edif} ({(salasPorEdificio[edif] || []).length})
                    </button>
                  ))}
                </div>

                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => scrollCarruselSalas(-1)}
                    aria-label="Anterior"
                    style={{
                      position: 'absolute', left: -14, top: '50%', transform: 'translateY(-50%)',
                      zIndex: 2, width: 34, height: 34, borderRadius: '50%', border: '1px solid #e2e8f0',
                      background: '#fff', boxShadow: '0 4px 14px rgba(15,23,42,0.12)', cursor: 'pointer',
                      fontWeight: 700, color: '#334155',
                    }}
                  >
                    ‹
                  </button>

                  <div
                    ref={carruselSalasRef}
                    style={{
                      display: 'flex',
                      gap: 14,
                      overflowX: 'auto',
                      scrollSnapType: 'x mandatory',
                      padding: '4px 6px 12px',
                    }}
                  >
                    {(salasPorEdificio[edificioActivoSalas] || []).length === 0 && (
                      <div style={{ color: '#64748b', padding: '20px 0' }}>No hay salas registradas en este edificio.</div>
                    )}
                    {(salasPorEdificio[edificioActivoSalas] || []).map((sala) => {
                      const estado = String(sala.estado || 'Disponible').trim();
                      const disponible = estado === 'Disponible';
                      const imagen = getSalaImagen(sala.edificio, sala.nombre_Sala);
                      return (
                        <div
                          key={sala.id_Sala}
                          style={{
                            flex: '0 0 250px',
                            scrollSnapAlign: 'start',
                            background: '#0f172a',
                            borderRadius: 14,
                            overflow: 'hidden',
                            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.18)',
                            color: '#fff',
                          }}
                        >
                          <div style={{ height: 140, background: '#1e293b' }}>
                            {imagen && (
                              <img
                                src={imagen}
                                alt={sala.nombre_Sala}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            )}
                          </div>

                          <div style={{ padding: '10px 12px 0' }}>
                            <div style={{ fontSize: 14, fontWeight: 800, textAlign: 'center', marginBottom: 8 }}>
                              {sala.nombre_Sala}
                            </div>
                            <div style={{ fontSize: 11, color: '#cbd5e1', lineHeight: 1.7 }}>
                              <div><strong style={{ color: '#fff' }}>Edificio:</strong> {sala.edificio}</div>
                              <div><strong style={{ color: '#fff' }}>Piso:</strong> {sala.n_Piso || 'N/A'}</div>
                              {sala.tamaño_Sala && (
                                <div><strong style={{ color: '#fff' }}>Tamaño:</strong> {sala.tamaño_Sala}</div>
                              )}
                              {sala.n_Personas != null && (
                                <div><strong style={{ color: '#fff' }}>Personas:</strong> {sala.n_Personas}</div>
                              )}
                            </div>

                            <div style={{ textAlign: 'center', margin: '10px 0' }}>
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '3px 12px',
                                  borderRadius: 20,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  background: disponible ? '#d1fae5' : '#fee2e2',
                                  color: disponible ? '#065f46' : '#991b1b',
                                }}
                              >
                                {estado}
                              </span>
                            </div>
                          </div>

                          <button
                            onClick={() => toggleEstadoSala(sala)}
                            disabled={salaActualizando === sala.id_Sala}
                            style={{
                              display: 'block',
                              width: '100%',
                              border: 'none',
                              padding: '10px 0',
                              fontWeight: 700,
                              fontSize: 12,
                              cursor: salaActualizando === sala.id_Sala ? 'not-allowed' : 'pointer',
                              opacity: salaActualizando === sala.id_Sala ? 0.6 : 1,
                              background: disponible
                                ? 'linear-gradient(90deg,#ef4444,#dc2626)'
                                : 'linear-gradient(90deg,#3da5ff,#2563eb)',
                              color: '#fff',
                            }}
                          >
                            {salaActualizando === sala.id_Sala
                              ? 'Actualizando…'
                              : disponible ? 'Marcar No Disponible' : 'Marcar Disponible'}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => scrollCarruselSalas(1)}
                    aria-label="Siguiente"
                    style={{
                      position: 'absolute', right: -14, top: '50%', transform: 'translateY(-50%)',
                      zIndex: 2, width: 34, height: 34, borderRadius: '50%', border: '1px solid #e2e8f0',
                      background: '#fff', boxShadow: '0 4px 14px rgba(15,23,42,0.12)', cursor: 'pointer',
                      fontWeight: 700, color: '#334155',
                    }}
                  >
                    ›
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <Modal
        size="lg"
        show={vistaModal}
        onHide={() => setVistaModal(false)}
        aria-labelledby="gestion-roles-modal-title"
        className="d-flex justify-content-center align-items-center"
      >
        <Modal.Header closeButton className="App">
          <Modal.Title id="gestion-roles-modal-title" style={{ width: '700px' }}>
            Administrar Rol de Usuario {correoUsuario}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="container d-flex justify-content-around">
            <FormGroup>
              <FormLabel style={{ marginBottom: '0px' }}>Rol de Usuario Actual</FormLabel>
              <FormControl type="text" style={{ width: '300px' }} disabled value={nombreRolActual} />
            </FormGroup>

            <FormGroup>
              <FormLabel style={{ marginBottom: '0px' }}>Nuevo Rol que Tendrá el Usuario</FormLabel>
              <FormSelect style={{ width: '300px' }} onChange={(e) => setNuevoRol(e.target.value)}>
                <option>Seleccione Una Opción</option>
                {roles.map((lista) => (
                  <option key={lista.nombre_Rol}>{lista.nombre_Rol}</option>
                ))}
              </FormSelect>
            </FormGroup>
          </div>

          <div className="container d-flex justify-content-center">
            <FormGroup>
              <Button style={{ width: '500px', marginTop: '20px' }} onClick={saveChange}>Guardar Cambio</Button>
            </FormGroup>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default Gestion;